import { listDisciplines } from "@shared/labs/disciplines/registry";
import {
  Archive,
  ArrowLeft,
  BookOpen,
  Building2,
  ClipboardList,
  DatabaseBackup,
  DollarSign,
  GraduationCap,
  HeartPulse,
  Layers,
  Library,
  ListChecks,
  Lock,
  PlayCircle,
  ScrollText,
  TrendingUp,
  UserCheck,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { PageHeader } from "@/components/page-header";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import "./styles.css";
import { AuditSection } from "./audit-section";
import { BackupSection } from "./backup-section";
import { CalibrationRunsSection } from "./calibration-runs-section";
import { CorpusSection } from "./corpus-section";
import { EvaluationsSection } from "./evaluations-section";
import { FrameworksSection } from "./frameworks-section";
import { LlmCostsSection } from "./llm-costs-section";
import { MarkdownSection } from "./markdown-section";
import { PromptHealthSection } from "./prompt-health-section";
import { PromptSection } from "./prompt-section";
import { usePageAudience } from "./use-page-audience";

/**
 * Evaluation settings — task-oriented surface for CHRO review.
 *
 * Four pillars, each answering a concrete CHRO question:
 *   I.  Disciplinas   — "onde eu edito as disciplinas?" Disciplines come first
 *       and alone; the six market-reference tables (companies, universities,
 *       etc.) are bundled under a single "Referências de mercado" accordion
 *       so the primary lever (disciplines) is one click away, not buried.
 *   II. Avaliações    — every completed assessment, grouped by discipline
 *       and recency, real names, detail overlay renders the payload in
 *       plain language.
 *   III. Calibração   — reference profiles, the batch-processing cockpit,
 *       and the correction audit trail (drop/revert lives on each row).
 *   IV. Avançado      — raw prompt templates. Engineering-only.
 *
 * What used to live here and was killed off (keep this list — future-me
 * will wonder):
 *   · "Rubrica de scoring" (scoring:methodology + scoring:examples + per-dim
 *     prompts) — the per-dim rescorer intentionally does not read methodology
 *     or examples (they describe Call-3's 0-1 matrix and collide with the
 *     1-5 scale the rescorer uses). Seed + disk files were removed with the
 *     redesign.
 *   · "Política de ajustes" (correction:policy:{user}) — every correction
 *     parks by default now, so the preview/auto/park selector had no effect.
 *     Hidden from UI; backend key stays for E2E fixture compatibility.
 */

interface PillarDef {
  id: string;
  kicker: string;
  title: string;
  blurb: string;
  items: AccordionEntry[];
  /** "chro" pillars show in both modes; "engineering" only shows in full
   *  mode. The CHRO mode is the default for everyone (see
   *  `usePageAudience`) — engineers flip to full mode when they need the
   *  prompt pipeline / backup tooling. */
  audience: "chro" | "engineering";
}

interface AccordionEntry {
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  body: React.ReactNode;
}

const PROMPTS: { value: string; label: string; description: string }[] = [
  {
    value: "prompt:candidate-evaluation-manual",
    label: "Análise geral do perfil",
    description: "Classificação, insights e recomendação inicial a partir do perfil bruto.",
  },
  {
    value: "prompt:call2-profile-extraction",
    label: "Extração de carreira · texto/PDF",
    description: "Estrutura carreira e educação quando o perfil chega como texto livre.",
  },
  {
    value: "prompt:call2-slim-profile-scoring",
    label: "Extração de carreira · LinkedIn",
    description: "Pontua o histórico quando os dados já vêm estruturados da API do LinkedIn.",
  },
  {
    value: "prompt:call3-scoring",
    label: "Matriz de scoring",
    description: "Produz a matriz de critérios com notas, percentil e resumo narrativo.",
  },
  {
    value: "prompt:call5-position-fit",
    label: "Fit com a posição",
    description: "Avalia o candidato contra o framework de skills da vaga alvo.",
  },
  {
    value: "calibration:prompt:system",
    label: "Agente de calibração",
    description:
      "Sistema que guia o agente a propor ajustes de rubrica quando a CHRO corrige uma nota. Codifica lições aprendidas (regras por padrão, precedência, guards por nível).",
  },
];

function MarketReferencesPanel() {
  return (
    <div className="market-refs py-2">
      <p className="text-sm text-muted-foreground max-w-prose mb-3">
        Tabelas de referência que o modelo lê durante a classificação inicial — empresas por tier,
        universidades, curvas de promoção, escopo técnico e força do referral. Editar aqui altera
        toda avaliação futura; não mexa sem motivo.
      </p>
      <Accordion type="single" collapsible className="space-y-2">
        <AccordionItem value="companies" className="border rounded-md px-3">
          <AccordionTrigger className="hover:no-underline py-3">
            <div className="flex items-center gap-3 text-left">
              <Building2 className="h-4 w-4 text-primary shrink-0" />
              <div>
                <div className="font-semibold text-sm">Empresas & eras</div>
                <div className="text-xs text-muted-foreground font-normal">
                  Tiers de empresas e eras conhecidas — o modelo separa Tier 1 atual de histórico.
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <MarkdownSection
              section="companies"
              placeholder="Ex: - **Wildlife Games** (Tier 1): 2018-2022 Exceptional, 2023-present Moderate"
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="universities" className="border rounded-md px-3">
          <AccordionTrigger className="hover:no-underline py-3">
            <div className="flex items-center gap-3 text-left">
              <GraduationCap className="h-4 w-4 text-primary shrink-0" />
              <div>
                <div className="font-semibold text-sm">Universidades</div>
                <div className="text-xs text-muted-foreground font-normal">
                  Classificação das instituições por tier acadêmico.
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <MarkdownSection
              section="universities"
              placeholder="Ex: - **Tier 1 — Top-tier federal/International:** UFMG, USP, UNICAMP, ..."
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="promotion_benchmarks" className="border rounded-md px-3">
          <AccordionTrigger className="hover:no-underline py-3">
            <div className="flex items-center gap-3 text-left">
              <TrendingUp className="h-4 w-4 text-primary shrink-0" />
              <div>
                <div className="font-semibold text-sm">Velocidade de promoção</div>
                <div className="text-xs text-muted-foreground font-normal">
                  Quão rápido é "rápido" para cada trajetória — alimenta crescimento.
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <MarkdownSection
              section="promotion_benchmarks"
              placeholder="Ex: - SWE → Sr. SWE: Accelerated <24mo, On track 24-36mo, Slow >36mo"
              rows={10}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="technical_scope" className="border rounded-md px-3">
          <AccordionTrigger className="hover:no-underline py-3">
            <div className="flex items-center gap-3 text-left">
              <Layers className="h-4 w-4 text-primary shrink-0" />
              <div>
                <div className="font-semibold text-sm">Escopo técnico</div>
                <div className="text-xs text-muted-foreground font-normal">
                  Referências de escopo por nível — escala de usuários, criticidade, complexidade.
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <MarkdownSection
              section="technical_scope"
              placeholder="Ex: - L3-L4: Platform >1M users OR significant transactions"
              rows={8}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="referral_signals" className="border rounded-md px-3">
          <AccordionTrigger className="hover:no-underline py-3">
            <div className="flex items-center gap-3 text-left">
              <UserCheck className="h-4 w-4 text-primary shrink-0" />
              <div>
                <div className="font-semibold text-sm">Sinais de referral</div>
                <div className="text-xs text-muted-foreground font-normal">
                  Peso de cada tipo de indicação — gestor direto vale mais que ex-colega distante.
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <MarkdownSection
              section="referral_signals"
              placeholder="Ex: | Direct manager, >2 years | Maximum |"
              rows={10}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

/**
 * Build the page's pillar list. The order returned here is the CHRO's
 * mental order: Calibração first (the daily-use action), then archived
 * evaluations (review), then disciplines (rubric). The Engenharia pillar
 * comes last and is filtered out in CHRO mode.
 *
 * Item order INSIDE Calibração also matches the CHRO's flow:
 *   1. Execução da calibração — the action they want to take first
 *   2. Histórico de correções — review what's been done
 *   3. Perfis de referência — reference data they consult occasionally
 */
/**
 * Two-mode audience toggle anchored next to the page header. CHRO mode is
 * the default for everyone — a CHRO landing here for the first time sees
 * only what they need; engineers flip to full mode and the choice
 * persists per browser via `usePageAudience`.
 *
 * Visual: a segmented control rather than a dropdown. The label is the
 * full mode name so the operator never has to remember which radio
 * position means what.
 */
function PageAudienceToggle({
  audience,
  onChange,
}: {
  audience: "chro" | "full";
  onChange: (next: "chro" | "full") => void;
}) {
  return (
    <div className="eval-settings-audience" role="radiogroup" aria-label="Modo de visualização">
      {/* biome-ignore lint/a11y/useSemanticElements: button + role=radio is the segmented-control pattern; native <input type=radio> doesn't support the visual treatment we need (pill background, click target = whole pill, no separate label) */}
      <button
        type="button"
        role="radio"
        aria-checked={audience === "chro"}
        className={`eval-settings-audience-pill${audience === "chro" ? " is-active" : ""}`}
        onClick={() => onChange("chro")}
        data-testid="audience-toggle-chro"
        title="Apenas o que a CHRO usa: calibração, histórico, avaliações e disciplinas"
      >
        Modo CHRO
      </button>
      {/* biome-ignore lint/a11y/useSemanticElements: see above — same segmented-control pattern */}
      <button
        type="button"
        role="radio"
        aria-checked={audience === "full"}
        className={`eval-settings-audience-pill${audience === "full" ? " is-active" : ""}`}
        onClick={() => onChange("full")}
        data-testid="audience-toggle-full"
        title="Inclui prompts do pipeline, backup e ferramentas de manutenção"
      >
        Modo completo
      </button>
    </div>
  );
}

interface PageDisciplineSelectorProps {
  value: string;
  options: Array<{ slug: string; name: string }>;
  onChange: (value: string) => void;
}

/**
 * Page-level discipline picker. Hidden when only one discipline is registered
 * so there's no UI clutter in the single-discipline state (today's reality).
 * Appears automatically once a second discipline is added to the registry.
 */
function PageDisciplineSelector({ value, options, onChange }: PageDisciplineSelectorProps) {
  if (options.length <= 1) return null;
  return (
    <select
      data-testid="page-discipline-filter"
      className="eval-settings-discipline-filter"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Filtrar por disciplina"
    >
      <option value="all">Todas as disciplinas</option>
      {options.map((d) => (
        <option key={d.slug} value={d.slug}>
          {d.name}
        </option>
      ))}
    </select>
  );
}

function pillars(discipline: string | undefined): PillarDef[] {
  return [
    {
      id: "calibracao",
      kicker: "I · calibração",
      title: "Calibração",
      blurb:
        "O que você usa todo dia: rodar calibrações, revisar correções aplicadas e parar correções que travaram. Os perfis de referência (fixtures) ficam logo abaixo para consulta.",
      audience: "chro",
      items: [
        {
          value: "calibration_runs",
          icon: PlayCircle,
          title: "Execução da calibração",
          subtitle:
            "Fila, execução em andamento, re-executar correções aguardando, e o botão para antecipar o processamento do lote.",
          body: <CalibrationRunsSection discipline={discipline} />,
        },
        {
          value: "corrections_audit",
          icon: ClipboardList,
          title: "Histórico de correções",
          subtitle:
            "Cada ajuste da CHRO com status e motivo. Reverter correções aplicadas ou descartar as que ainda estão na fila.",
          body: <AuditSection discipline={discipline} />,
        },
        {
          value: "calibration_corpus",
          icon: ListChecks,
          title: "Perfis de referência",
          subtitle:
            "Exemplos que testam toda correção antes de ela virar ajuste real. Alterne entre evidências e skills para revisar notas esperadas.",
          body: <CorpusSection discipline={discipline} />,
        },
      ],
    },
    {
      id: "avaliacoes",
      kicker: "II · histórico",
      title: "Avaliações arquivadas",
      blurb:
        "Toda avaliação que rodamos fica guardada aqui, agrupada por disciplina e ordenada da mais recente à mais antiga. Nome real, relatório em linguagem natural no clique — JSON bruto por link.",
      audience: "chro",
      items: [
        {
          value: "evaluations_archive",
          icon: Archive,
          title: "Arquivo de avaliações",
          subtitle:
            "Por disciplina, por data, com nome real. Abrir uma linha mostra o relatório parsed em linguagem natural; o JSON bruto fica acessível por link.",
          body: <EvaluationsSection discipline={discipline} />,
        },
      ],
    },
    {
      id: "disciplinas",
      kicker: "III · disciplinas",
      title: "Disciplinas",
      blurb:
        "A lente por onde o modelo lê o mercado. Editar aqui altera toda avaliação subsequente — mexa quando quiser ajustar o que define cada nível ou trocar a referência de mercado de fundo.",
      audience: "chro",
      items: [
        {
          value: "frameworks",
          icon: BookOpen,
          title: "Disciplinas & skills",
          subtitle:
            "Skills esperadas por nível, por disciplina. Adicione, renomeie ou reescreva rubricas.",
          body: <FrameworksSection />,
        },
        {
          value: "market_references",
          icon: Library,
          title: "Referências de mercado",
          subtitle:
            "Tabelas secundárias: empresas, universidades, curvas de promoção, escopo técnico e referrals.",
          body: <MarketReferencesPanel />,
        },
      ],
    },
    {
      id: "avancado",
      kicker: "IV · engenharia",
      title: "Avançado",
      blurb:
        "Templates de prompt individuais do pipeline + ferramentas de manutenção. Útil para debugar — mexer aqui afeta toda avaliação subsequente.",
      audience: "engineering",
      items: [
        {
          value: "prompts",
          icon: ScrollText,
          title: "Prompts do pipeline",
          subtitle: "Templates brutos de cada etapa da avaliação.",
          body: (
            <Accordion type="single" collapsible className="space-y-3 pt-2">
              {PROMPTS.map(({ value, label, description }) => (
                <AccordionItem key={value} value={value} className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="text-left">
                      <div className="font-medium">{label}</div>
                      <div className="text-sm text-muted-foreground font-normal">{description}</div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <PromptSection section={value} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ),
        },
        {
          value: "prompt_health",
          icon: HeartPulse,
          title: "Saúde dos prompts de calibração",
          subtitle:
            "Cada rubrica scoring:dim:<dim> que o agente lê. Quando uma está vazia, todas as notas daquela dimensão saem 1.00 — esta tela detecta o problema antes de você rodar uma calibração.",
          body: <PromptHealthSection />,
        },
        {
          value: "llm_costs",
          icon: DollarSign,
          title: "Custos LLM",
          subtitle:
            "Quanto cada avaliação de perfil e cada calibração custou em chamadas de LLM. Totais móveis (24h / 7d / 30d), breakdown por superfície e drill-down por correção / avaliação.",
          body: <LlmCostsSection />,
        },
        {
          value: "backup",
          icon: DatabaseBackup,
          title: "Backup / restore das tabelas do labs",
          subtitle:
            "Baixa o snapshot das 5 tabelas como JSON ou carrega um snapshot baixado para reproduzir um estado de outro ambiente. Restaurar exige super-admin e nunca roda em produção.",
          body: <BackupSection />,
        },
      ],
    },
  ];
}

export default function EvaluationSettings() {
  const { isSuperAdmin, isManager, hasLabsAccess } = useAuth();
  // Mirrors the server's requireLabsAccess: super-admin OR manager OR a user
  // with the labs SpiceDB role grant. Anyone who passes the server gate
  // should get the page; the prior admin/manager-only check 403'd labs-role
  // users here even though their API requests would have succeeded.
  const canAccess = isSuperAdmin || isManager || hasLabsAccess;
  const [audience, setAudience] = usePageAudience();
  const [pageDiscipline, setPageDiscipline] = useState<string>("all");
  const disciplines = useMemo(() => listDisciplines(), []);
  const effectiveDiscipline = pageDiscipline === "all" ? undefined : pageDiscipline;
  // CHRO mode strips the engineering pillar entirely. Reorder is a no-op
  // in CHRO mode (Calibração already first); engineering shows up at the
  // end in full mode.
  const allPillars = pillars(effectiveDiscipline);
  const pillarList =
    audience === "chro" ? allPillars.filter((p) => p.audience === "chro") : allPillars;
  if (!canAccess) {
    return (
      <div className="eval-settings-shell">
        <div className="flex items-center gap-3">
          <Link href="/labs/candidate-intelligence">
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <PageHeader
            title="Configurações de avaliação"
            description="Área restrita — administradores, gestores e usuários com acesso ao Labs."
          />
        </div>
        <div
          className="mt-8 rounded-lg border border-dashed p-10 text-center max-w-xl mx-auto"
          data-testid="eval-settings-access-denied"
        >
          <Lock className="h-8 w-8 mx-auto mb-3 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-base font-semibold">Acesso restrito</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Esta tela permite editar rubricas, prompts, corpus de calibração e outras configurações
            compartilhadas. Por segurança, só administradores, gestores com liderados diretos e
            usuários com acesso ao Labs conseguem abrir.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Você pode seguir usando a{" "}
            <Link href="/labs/candidate-intelligence" className="text-primary hover:underline">
              Inteligência de Candidatos
            </Link>{" "}
            normalmente.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="eval-settings-shell" data-audience={audience}>
      <div className="flex items-center gap-3 flex-wrap">
        <Link href="/labs/candidate-intelligence">
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <PageHeader
          title="Configurações de avaliação"
          description={
            audience === "chro"
              ? "Calibrações, correções e disciplinas — o essencial para a operação do dia."
              : "Visão completa: operação + ferramentas técnicas (prompts, backup, saúde dos prompts)."
          }
        />
        <PageAudienceToggle audience={audience} onChange={setAudience} />
        <PageDisciplineSelector
          value={pageDiscipline}
          options={disciplines}
          onChange={setPageDiscipline}
        />
      </div>

      <nav className="eval-settings-toc" aria-label="Atalhos">
        {pillarList.map((p) => (
          <a key={p.id} href={`#${p.id}`} className="eval-settings-toc-link">
            <span className="eval-settings-toc-kicker">{p.kicker}</span>
            <span className="eval-settings-toc-title">{p.title}</span>
          </a>
        ))}
      </nav>

      <div className="space-y-14">
        {pillarList.map((p) => (
          <section key={p.id} id={p.id} className="eval-settings-section">
            <header className="eval-settings-section-header">
              <span className="eval-settings-kicker">{p.kicker}</span>
              <h2 className="eval-settings-title">{p.title}</h2>
              <p className="eval-settings-blurb">{p.blurb}</p>
            </header>
            <Accordion type="single" collapsible className="space-y-3">
              {p.items.map((item) => {
                const Icon = item.icon;
                return (
                  <AccordionItem
                    key={item.value}
                    value={item.value}
                    className="border rounded-lg px-4"
                  >
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-primary" />
                        <div className="text-left">
                          <div className="font-semibold">{item.title}</div>
                          <div className="text-sm text-muted-foreground font-normal">
                            {item.subtitle}
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>{item.body}</AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </section>
        ))}
      </div>
    </div>
  );
}
